import React, { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { MAX_DATE } from 'constants/common';
import { DATE_FORMAT_MM_YYYY } from 'constants/formats';
import dayjs from 'dayjs';

import { AutocompleteControl } from 'components/_basic/Form/Autocomplete';
import { DatePickerControl } from 'components/_basic/Form/DatePicker/DatePickerControl';
import { RemoveButton } from 'components/_basic/Form/FieldArray/components/RemoveButton';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { Responsive } from 'components/_basic/Responsive';
import { BorderedCard } from 'components/BorderedCard';
import { STATUS_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { STATUS_OPTIONS } from 'components/BusinessToolForm/constants/options';
import { STATUS_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';

import { getDayJsDate } from 'helpers/formatDates';
import { concatWithDot } from 'helpers/string';

import { STAGES_NAMES } from '../../utils/form';

export const StagesComponent: React.FC<{
  name: string;
  hiddenLabel?: boolean;
  multiline?: boolean;
  maxLength?: number;
  disabled?: boolean;
  onRemove?: VoidFunction;
}> = memo(({ name, disabled, hiddenLabel, multiline, maxLength, onRemove }) => {
  const { t, i18n } = useTranslation();
  const { control, getValues } = useFormContext();

  const fromDateValue = getValues(concatWithDot(name, STAGES_NAMES.FROM)) || undefined;
  const toDateValue = getValues(concatWithDot(name, STAGES_NAMES.TO)) || undefined;

  const getChosenStatusOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return STATUS_OPTIONS_DEU;

      case 'uk':
        return STATUS_OPTIONS_UA;

      default:
        return STATUS_OPTIONS;
    }
  };

  return (
    <BorderedCard>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextInputControl
            control={control}
            id={concatWithDot(name, STAGES_NAMES.STAGE)}
            name={concatWithDot(name, STAGES_NAMES.STAGE)}
            label={t('implementationPlan.stages.stage')}
            disabled={disabled}
            maxLength={maxLength}
            multiline={multiline}
            hiddenLabel={hiddenLabel}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <DatePickerControl
            control={control}
            id={concatWithDot(name, STAGES_NAMES.FROM)}
            name={concatWithDot(name, STAGES_NAMES.FROM)}
            label={t('implementationPlan.stages.from')}
            disabled={disabled}
            dateFormat={DATE_FORMAT_MM_YYYY}
            views={['month', 'year']}
            maxDate={getDayJsDate(toDateValue)}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <DatePickerControl
            id={concatWithDot(name, STAGES_NAMES.TO)}
            name={concatWithDot(name, STAGES_NAMES.TO)}
            label={t('implementationPlan.stages.to')}
            maxDate={dayjs(MAX_DATE)}
            disabled={disabled}
            dateFormat={DATE_FORMAT_MM_YYYY}
            views={['month', 'year']}
            minDate={getDayJsDate(fromDateValue)}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <AutocompleteControl
            control={control}
            id={concatWithDot(name, STAGES_NAMES.STATUS)}
            name={concatWithDot(name, STAGES_NAMES.STATUS)}
            label={t('implementationPlan.stages.status')}
            options={getChosenStatusOptions(i18n.language)}
            defaultValue={getChosenStatusOptions(i18n.language)[0]}
            disabled={disabled}
          />
        </Grid>
        <Responsive mobile>
          <Grid item xs={12}>
            <RemoveButton onClick={onRemove} disabled={disabled} canRemove>
              Delete
            </RemoveButton>
          </Grid>
        </Responsive>
      </Grid>
    </BorderedCard>
  );
});
